import React, { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Tracks which drafts (by id) currently hold unsaved text, so `TaskDetailsDrawer` can ask before
 * closing over one. Owned by the drawer; `TaskDescription` and `TaskComments` register their own
 * dirty state through `useDirtyGuard`.
 */
export type DirtyRegistry = {
  register: (id: string, isDirty: boolean) => void;
  hasDirty: () => boolean;
};

export function createDirtyRegistry(): DirtyRegistry {
  const dirtyIds = new Set<string>();
  return {
    register: (id, isDirty) => {
      if (isDirty) {
        dirtyIds.add(id);
      } else {
        dirtyIds.delete(id);
      }
    },
    hasDirty: () => dirtyIds.size > 0,
  };
}

const DirtyContext = createContext<DirtyRegistry | null>(null);

export const DirtyGuardProvider: React.FC<{ registry: DirtyRegistry; children: React.ReactNode }> = ({
  registry,
  children,
}) => <DirtyContext.Provider value={registry}>{children}</DirtyContext.Provider>;

/** Reports `id`'s current dirty state to the nearest `DirtyGuardProvider` on every change. */
export function useDirtyGuard(id: string, isDirty: boolean): void {
  const registry = useContext(DirtyContext);
  const registryRef = useRef(registry);
  registryRef.current = registry;

  useEffect(() => {
    registryRef.current?.register(id, isDirty);
  }, [id, isDirty]);

  // Unmounting (Save/Cancel closed the editor, or the drawer itself unmounted) clears the flag.
  useEffect(() => () => registryRef.current?.register(id, false), [id]);
}
